import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core';

/**
 * A service to manage user interface preferences, providing reactive signals
 * for each preference. All preferences are persisted in localStorage.
 */
@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  // -------------------------------------------------------------------------
  // Floating Page Header
  // -------------------------------------------------------------------------

  /**
   * Internal signal holding whether the page header should float (sticky) on scroll.
   */
  private readonly floatingHeaderSignal: WritableSignal<boolean> = signal(false);

  /**
   * Read-only signal indicating whether the floating page header is enabled.
   */
  public readonly floatingHeader: Signal<boolean> = computed(() => this.floatingHeaderSignal());

  // -------------------------------------------------------------------------
  // Floating Table Header
  // -------------------------------------------------------------------------

  /**
   * Internal signal holding whether table column headers should float (sticky) on scroll.
   * Only applies to tables that have opted in via `floatingHeader: true` in their TableConfig.
   */
  private readonly floatingTableHeaderSignal: WritableSignal<boolean> = signal(false);

  /**
   * Read-only signal indicating whether the floating table header preference is enabled.
   */
  public readonly floatingTableHeader: Signal<boolean> = computed(() => this.floatingTableHeaderSignal());

  // -------------------------------------------------------------------------
  // Table Export (file download)
  // -------------------------------------------------------------------------

  /**
   * Internal signal holding whether the CSV file export action is enabled.
   * Defaults to true — disabling removes the export button from all tables.
   */
  private readonly tableExportSignal: WritableSignal<boolean> = signal(true);

  /**
   * Read-only signal indicating whether the table file export is enabled.
   */
  public readonly tableExport: Signal<boolean> = computed(() => this.tableExportSignal());

  // -------------------------------------------------------------------------
  // Table Clipboard (copy to clipboard)
  // -------------------------------------------------------------------------

  /**
   * Internal signal holding whether the CSV copy-to-clipboard action is enabled.
   * Defaults to true — disabling removes the clipboard button from all tables.
   */
  private readonly tableClipboardSignal: WritableSignal<boolean> = signal(true);

  /**
   * Read-only signal indicating whether the table clipboard export is enabled.
   */
  public readonly tableClipboard: Signal<boolean> = computed(() => this.tableClipboardSignal());

  // -------------------------------------------------------------------------
  // Dashboard: Computed Grid Loss table (BETA)
  // -------------------------------------------------------------------------

  /**
   * Internal signal holding whether the computed grid loss table is visible on the dashboard.
   * Disabled by default — this is a BETA feature.
   */
  private readonly dashboardComputedGridlossSignal: WritableSignal<boolean> = signal(false);

  /**
   * Read-only signal indicating whether the computed grid loss table is shown on the dashboard.
   */
  public readonly dashboardComputedGridloss: Signal<boolean> = computed(() => this.dashboardComputedGridlossSignal());

  constructor() {
    this.floatingHeaderSignal.set(
      localStorage.getItem('floatingHeader') === 'true'
    );
    this.floatingTableHeaderSignal.set(
      localStorage.getItem('floatingTableHeader') === 'true'
    );
    this.tableExportSignal.set(
      localStorage.getItem('tableExport') !== 'false'
    );
    this.tableClipboardSignal.set(
      localStorage.getItem('tableClipboard') !== 'false'
    );
    this.dashboardComputedGridlossSignal.set(
      localStorage.getItem('dashboardComputedGridloss') === 'true'
    );
  }

  // -------------------------------------------------------------------------
  // Floating Page Header Methods
  // -------------------------------------------------------------------------


  /**
   * Sets the floating page header preference and persists it in localStorage.
   * @param floatingHeader - Whether the page header should float (sticky) on scroll.
   */
  public setFloatingHeader(floatingHeader: boolean): void {
    this.floatingHeaderSignal.set(floatingHeader);
    localStorage.setItem('floatingHeader', floatingHeader ? 'true' : 'false');
  }

  /**
   * Enables the floating (sticky) page header and persists the preference.
   */
  public enableFloatingHeader(): void {
    this.floatingHeaderSignal.set(true);
    localStorage.setItem('floatingHeader', 'true');
  }

  /**
   * Disables the floating page header and persists the preference.
   */
  public disableFloatingHeader(): void {
    this.floatingHeaderSignal.set(false);
    localStorage.setItem('floatingHeader', 'false');
  }

  /**
   * Toggles the floating page header preference.
   */
  public toggleFloatingHeader(): void {
    const current = this.floatingHeaderSignal();
    this.floatingHeaderSignal.set(!current);
    localStorage.setItem('floatingHeader', current ? 'false' : 'true');
  }

  // -------------------------------------------------------------------------
  // Floating Table Header Methods
  // -------------------------------------------------------------------------

  public setFloatingTableHeader(floatingTableHeader: boolean): void {
    this.floatingTableHeaderSignal.set(floatingTableHeader);
    localStorage.setItem('floatingTableHeader', floatingTableHeader ? 'true' : 'false');
  }

  /**
   * Enables floating (sticky) table column headers and persists the preference.
   */
  public enableFloatingTableHeader(): void {
    this.floatingTableHeaderSignal.set(true);
    localStorage.setItem('floatingTableHeader', 'true');
  }

  /**
   * Disables floating table column headers and persists the preference.
   */
  public disableFloatingTableHeader(): void {
    this.floatingTableHeaderSignal.set(false);
    localStorage.setItem('floatingTableHeader', 'false');
  }

  /**
   * Toggles the floating table header preference.
   */
  public toggleFloatingTableHeader(): void {
    const current = this.floatingTableHeaderSignal();
    this.floatingTableHeaderSignal.set(!current);
    localStorage.setItem('floatingTableHeader', current ? 'false' : 'true');
  }

  // -------------------------------------------------------------------------
  // Table Export Methods
  // -------------------------------------------------------------------------

  /**
   * Sets whether the CSV file export action is shown on tables and persists it.
   */
  public setTableExport(enabled: boolean): void {
    this.tableExportSignal.set(enabled);
    localStorage.setItem('tableExport', enabled ? 'true' : 'false');
  }

  // -------------------------------------------------------------------------
  // Table Clipboard Methods
  // -------------------------------------------------------------------------

  /**
   * Sets whether the CSV copy-to-clipboard action is shown on tables and persists it.
   */
  public setTableClipboard(enabled: boolean): void {
    this.tableClipboardSignal.set(enabled);
    localStorage.setItem('tableClipboard', enabled ? 'true' : 'false');
  }

  // -------------------------------------------------------------------------
  // Dashboard: Computed Grid Loss Methods
  // -------------------------------------------------------------------------

  /**
   * Sets whether the computed grid loss table is shown on the dashboard and persists it.
   */
  public setDashboardComputedGridloss(enabled: boolean): void {
    this.dashboardComputedGridlossSignal.set(enabled);
    localStorage.setItem('dashboardComputedGridloss', enabled ? 'true' : 'false');
  }
}
